import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Users, Package, Clock, LogOut, ArrowLeftRight, FileText, PhoneCall, Boxes, ShieldCheck, BarChart3, Building2, BookOpen, Menu, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import AdminAnalytics from '@/components/AdminAnalytics';
import AdminInventory from '@/components/AdminInventory';
import AdminTeam from '@/components/AdminTeam';
import AdminUsers from '@/components/AdminUsers';
import AdminCallbackRequests from '@/components/AdminCallbackRequests';
import AdminKYCQueue from '@/components/AdminKYCQueue';
import AdminBottomNav from '@/components/AdminBottomNav';
import AdminSideNav from '@/components/AdminSideNav';
import AdminOrgs from '@/components/AdminOrgs';
import AdminTally from '@/components/AdminTally';
import AdminRequirements from '@/components/AdminRequirements';

const TALLY_ENABLED = process.env.REACT_APP_FEATURE_TALLY !== 'false';

const SECTION_META = {
  kyc:          { title: 'KYC Applications', icon: FileText },
  users:        { title: 'Users', icon: Users },
  requests:     { title: 'Callback Requests', icon: PhoneCall },
  requirements: { title: 'Buyer Requirements', icon: ShoppingBag },
  inventory:    { title: 'Inventory', icon: Boxes },
  orgs:         { title: 'Organizations', icon: Building2 },
  ...(TALLY_ENABLED ? { tally: { title: 'Tally Sync', icon: BookOpen } } : {}),
  team:         { title: 'Admin Team', icon: ShieldCheck },
  analytics:    { title: 'Analytics', icon: BarChart3 },
};

export default function AdminPage() {
  const { api, logout, isAdminOnlySession } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Section lives in the URL so browser back (from /u/:id) restores it verbatim
  const rawSection = searchParams.get('section') || 'kyc';
  const section = SECTION_META[rawSection] ? rawSection : 'kyc';
  const setSection = useCallback((v) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('section', v);
      // Clear filter params when switching to a different section
      if (v !== rawSection) {
        next.delete('q');
        next.delete('role');
      }
      return next;
    }, { replace: false });
  }, [setSearchParams, rawSection]);
  const [stats, setStats] = useState(null);
  const [sideNavOpen, setSideNavOpen] = useState(false);
  const canSwitchToSeller = !isAdminOnlySession();

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/stats');
      setStats(data);
    } catch {
      toast.error('Failed to load stats');
    }
  }, [api]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const meta = SECTION_META[section] || SECTION_META.kyc;
  const SectionIcon = meta.icon;

  const renderSection = () => {
    switch (section) {
      case 'users':     return <AdminUsers />;
      case 'requests':  return <AdminCallbackRequests />;
      case 'requirements': return <AdminRequirements />;
      case 'inventory': return <AdminInventory />;
      case 'orgs':      return <AdminOrgs />;
      case 'tally':     return TALLY_ENABLED ? <AdminTally /> : <AdminKYCQueue />;
      case 'team':      return <AdminTeam />;
      case 'analytics': return <AdminAnalytics />;
      case 'kyc':
      default:          return <AdminKYCQueue />;
    }
  };

  return (
    <div
      className="w-full max-w-md mx-auto min-h-screen bg-[#F6F8FB] md:max-w-xl lg:max-w-2xl border-x border-[#E4E8EF]"
      data-testid="admin-page"
    >
      {/* Header — sized to match seller AppShell */}
      <header className="sticky top-0 z-40 bg-[#F6F8FB]/80 backdrop-blur-xl border-b border-[#E4E8EF] px-4 py-3 flex items-center gap-3">
        <button
          data-testid="admin-menu-btn"
          onClick={() => setSideNavOpen(true)}
          aria-label="Open menu"
          className="w-9 h-9 rounded-full hover:bg-[#EFF2F7] flex items-center justify-center transition-colors flex-shrink-0"
        >
          <Menu className="w-5 h-5 text-[#0B1620]" />
        </button>
        <SectionIcon className="w-5 h-5 text-[#0B4F3F] flex-shrink-0" />
        <h1 className="text-lg font-semibold text-[#0B1620] font-['Bricolage_Grotesque'] flex-1 min-w-0 truncate">{meta.title}</h1>
        <div className="flex items-center gap-2 flex-shrink-0">
          {canSwitchToSeller && (
            <button
              data-testid="switch-to-seller-btn"
              onClick={() => navigate('/')}
              className="text-[11px] font-semibold text-[#0B4F3F] bg-[#0B4F3F]/10 hover:bg-[#0B4F3F]/15 rounded-full px-2.5 py-1 flex items-center gap-1 transition-colors"
            >
              <ArrowLeftRight className="w-3.5 h-3.5" /> Seller view
            </button>
          )}
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            aria-label="Logout"
            className="w-9 h-9 rounded-full hover:bg-[#B91C1C]/5 text-[#5B6675] hover:text-[#B91C1C] transition-colors flex items-center justify-center"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="p-4 pb-24 space-y-5">
        {/* Platform Stats (always visible) */}
        {stats && (
          <div className="grid grid-cols-3 gap-3 animate-fade-in-up">
            <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 text-center">
              <Users className="w-5 h-5 mx-auto text-[#0B4F3F] mb-1" />
              <p className="text-xl font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{stats.total_users}</p>
              <p className="text-[10px] text-[#5B6675]">Users</p>
            </div>
            <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 text-center">
              <Package className="w-5 h-5 mx-auto text-[#0B4F3F] mb-1" />
              <p className="text-xl font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{stats.total_produce}</p>
              <p className="text-[10px] text-[#5B6675]">Produce</p>
            </div>
            <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 text-center">
              <Clock className="w-5 h-5 mx-auto text-[#0B4F3F] mb-1" />
              <p className="text-xl font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{stats.pending_kyc}</p>
              <p className="text-[10px] text-[#5B6675]">Pending</p>
            </div>
          </div>
        )}

        {renderSection()}
      </div>

      <AdminBottomNav active={section} onChange={setSection} />
      <AdminSideNav open={sideNavOpen} onOpenChange={setSideNavOpen} active={section} onSelect={setSection} />
    </div>
  );
}
